import { SimulationsSummary } from '@/client/campaigns';
import { FlexContainer } from '@/ui/styled-ui';
import {
  ActiveSimulationsIcon,
  PhishingSignsScheduledIcon,
  ScheduledSimulationsIcon,
  UnscheduledSimulationsIcon,
} from '@/utils/helpers/icons.helper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type InfoCardProps = {
  title: string;
  value: string | number;
  description: string;
  icon: React.ReactNode;
  className?: string;
};

const CardsContainer = styled(FlexContainer)`
  gap: var(--medium-padding);
  margin-bottom: var(--default-padding);
`;

const Card = styled.div`
  flex: 1;
  border-radius: var(--semi-medium-border-radius);
  border: 1px solid var(--green-radar-light);
  padding: var(--default-padding);
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 140px;

  hr {
    width: 100%;
    height: 0;
    border: 0;
    border-bottom: 1px solid;
    margin: 0;
  }

  &.active-simulations {
    border-color: var(--gray-moderate);

    hr {
      border-color: var(--gray-moderate);
    }
  }

  &.scheduled-simulations {
    background-color: var(--green-chart-pale);

    hr {
      border-color: var(--green-radar-light);
    }
  }

  &.unscheduled-simulations {
    background-color: var(--green-color);
    color: var(--white-main);

    hr {
      border-color: var(--teal-smoke);
    }
  }

  &.phishing-signs-scheduled {
    background-color: var(--green-chart-dark);
    color: var(--white-main);

    hr {
      border-color: var(--green-color);
    }
  }
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--xxsmall-border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white-main);
  font-size: var(--semi-big-font-size);
  flex-shrink: 0;
  background-color: var(--green-opacity);

  &.unscheduled-simulations {
    background-color: var(--cyan-pale);
  }

  &.phishing-signs-scheduled {
    background-color: var(--cyan-pale);
  }
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: var(--small-font-size);
  font-style: normal;
  font-weight: 400;
  line-height: var(--medium-line-height);
`;

const CardValue = styled.div`
  font-size: var(--card-value-font-size);
  font-weight: 700;
  line-height: var(--card-value-line-height);
  margin: 0;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: var(--xxsmall-font-size);
  line-height: var(--xxsmall-line-height);
  margin-top: var(--xxxsmall-padding);
`;

const InfoCard: React.FC<InfoCardProps> = ({
  title,
  value,
  description,
  icon,
  className,
}) => {
  return (
    <Card className={className}>
      <FlexContainer align="center" justify="flex-start" gap={8}>
        <IconContainer className={className}>{icon}</IconContainer>
        <CardTitle>{title}</CardTitle>
      </FlexContainer>
      <CardValue>{value}</CardValue>
      <hr />
      <CardDescription dangerouslySetInnerHTML={{ __html: description }} />
    </Card>
  );
};

export const InfoCards: React.FC<{
  autoPhishingSummary?: SimulationsSummary;
}> = ({ autoPhishingSummary }) => {
  const { t } = useTranslation();
  const { activeCount, scheduledCount, draftCount, phishingSignsSummary } =
    autoPhishingSummary || {};

  return (
    <CardsContainer align="stretch">
      <InfoCard
        title={t('phishing.simulations.infoCards.active.title')}
        description={t('phishing.simulations.infoCards.active.description')}
        icon={<ActiveSimulationsIcon />}
        className="active-simulations"
        value={activeCount || 0}
      />
      <InfoCard
        title={t('phishing.simulations.infoCards.scheduled.title')}
        description={t('phishing.simulations.infoCards.scheduled.description')}
        icon={<ScheduledSimulationsIcon />}
        className="scheduled-simulations"
        value={scheduledCount || 0}
      />
      <InfoCard
        title={t('phishing.simulations.infoCards.unscheduled.title')}
        description={t(
          'phishing.simulations.infoCards.unscheduled.description',
        )}
        icon={<UnscheduledSimulationsIcon />}
        className="unscheduled-simulations"
        value={draftCount || 0}
      />
      <InfoCard
        title={t('phishing.simulations.infoCards.phishingSigns.title')}
        value={`${phishingSignsSummary?.usedSigns?.length || 0}/8`}
        description={t('phishing.simulations.infoCards.missingSigns', {
          signs: phishingSignsSummary?.missingSigns
            ?.map((sign) => t(`campaign.template.${sign}`))
            .join(', '),
        })}
        icon={<PhishingSignsScheduledIcon />}
        className="phishing-signs-scheduled"
      />
    </CardsContainer>
  );
};
